import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { DialogBody } from '@m/alchemy-ui/DialogBody';
import { DialogFooter } from '@m/alchemy-ui/DialogFooter';
import { DialogHeader } from '@m/alchemy-ui/DialogHeader';
import { Field } from '@m/alchemy-ui/Field';
import { Input } from '@m/alchemy-ui/Input';
import { Toast } from '@m/alchemy-ui/Toast';

import { RootState } from 'common/store/types';

import server from 'common/utils/server';
import {
  useToasts,
  generateAutoDismissMessage
} from 'settings/hooks/useToasts';

import {
  SettingsDashboardItem,
  SettingsDashboardItemProps
} from '../SettingsDashboardItem';

function ChurnCompanyDialog({
  isOpen,
  onDismiss,
  onAccept,
}: ChurnCompanyDialogProps) {
  const [inputDomain, setInputDomain] = useState<string>('');
  const subdomain = useSelector<RootState, string>(state => state.company.url);

  const handleDismiss = () => {
    setInputDomain('');
    onDismiss();
  };

  if (!isOpen) return null;

  return (
    <Dialog isOpen size="medium">
      <DialogHeader>
        Are you sure you want to churn <span data-notranslate>{subdomain}</span>
        ?
      </DialogHeader>

      <DialogBody>
        <Field
          required
          label="Please type the name of the company's subdomain below:"
          input={<Input onChange={ev => setInputDomain(ev.target.value)} />}
          isFullWidth
        />
      </DialogBody>

      <DialogFooter
        footerButtons={[
          {
            label: 'Cancel',
            onClick: handleDismiss,
            priority: 'tertiary',
          },
          {
            label: 'I understand the consequences, churn this company',
            onClick: () => inputDomain === subdomain && onAccept(),
            disabled: inputDomain !== subdomain,
            palette: 'expressiveNegative',
          },
        ]}
      />
    </Dialog>
  );
}

interface ChurnCompanyDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAccept: () => void;
}

export function ChurnCompanyDashboardItem(props: SettingsDashboardItemProps) {
  const { messages, showMessage } = useToasts();
  const [isOpen, setOpen] = useState<boolean>(false);

  const closeModal = () => setOpen(false);

  const disableCompany = () => {
    closeModal();

    // We're receiving the disable URL through the link prop
    const disableUrl = props.link;
    server
      .patch(disableUrl, undefined, { format: 'json' })
      .then((data: { detail: string }) => {
        const message = generateAutoDismissMessage({ content: data.detail });
        showMessage(message);

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      });
  };

  return (
    <>
      <Toast messages={messages} />

      <SettingsDashboardItem
        id={props.id}
        title={props.title}
        subtitle={props.subtitle}
        onClick={() => setOpen(true)}
      />

      <ChurnCompanyDialog
        isOpen={isOpen}
        onDismiss={closeModal}
        onAccept={disableCompany}
      />
    </>
  );
}
